/**
 * Renders the reactions bar under a message bubble + the small floating action
 * row (Reply, React, Edit, Delete, View history) on hover.
 *
 * Used inside <ChatMessage />.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Reply, Smile, Edit2, Trash2, MoreHorizontal, Eye, X, Loader2 } from "lucide-react";

const REACTION_SET = ["❤️", "👍", "😂", "😮", "😢", "😡", "🙏", "🔥"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReactionsBar = ({ reactions, onReact, viewerRole, isOwn }) => {
  const entries = Object.entries(reactions || {}).filter(([, arr]) => arr && arr.length > 0);
  if (entries.length === 0) return null;
  return (
    <div data-testid="reactions-bar" className="flex flex-wrap gap-1 -mt-1 mb-1" style={{ marginLeft: isOwn ? "auto" : 0 }}>
      {entries.map(([emoji, actors]) => {
        const myActorId = viewerRole === "admin" ? "admin" : null;
        const reactedByMe = actors.includes("admin") && viewerRole === "admin"; // simplistic check (full check requires user_id)
        return (
          <button
            key={emoji}
            type="button"
            data-testid={`reaction-pill-${emoji}`}
            onClick={() => onReact?.(emoji)}
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border text-[10px] ${reactedByMe ? "border-[#d4af37] bg-[#d4af37]/10" : "border-white/10 bg-black/40"} hover:scale-105 transition`}
            title="Click para reaccionar igual / quitar"
          >
            <span>{emoji}</span>
            <span className="text-neutral-300">{actors.length}</span>
          </button>
        );
      })}
    </div>
  );
};


export const MessageActions = ({ msg, isOwn, viewerRole, authHeader, onReact, onReply, onEdit, onDelete, onShowHistory }) => {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false); setPickerOpen(false);
      }
    };
    if (open || pickerOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, pickerOpen]);

  // Eligibility
  const canEdit = msg.kind === "text" && !msg.deleted && (viewerRole === "admin" || isOwn);
  const canDelete = !msg.deleted && (viewerRole === "admin" || (isOwn && withinMinute(msg.created_at)));
  const canViewHistory = viewerRole === "admin" && (msg.edited || (msg.edit_history && msg.edit_history.length > 0));

  return (
    <div ref={menuRef} className={`absolute -top-7 ${isOwn ? "right-0" : "left-0"} flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition`}>
      {/* Quick react picker (smile + 5 options) */}
      <div className="relative">
        <button
          type="button"
          data-testid={`react-${msg.id}`}
          onClick={() => setPickerOpen((p) => !p)}
          className="h-6 w-6 rounded-full bg-[#1a1a1a] border border-white/10 hover:border-[#d4af37]/40 text-neutral-300 flex items-center justify-center"
          title="Reaccionar"
        ><Smile className="w-3 h-3" /></button>
        {pickerOpen && (
          <div data-testid={`react-picker-${msg.id}`} className={`absolute bottom-7 ${isOwn ? "right-0" : "left-0"} flex gap-0.5 bg-[#1a1a1a] border border-[#d4af37]/30 rounded-full px-1.5 py-1 shadow-2xl z-[110]`}>
            {REACTION_SET.map((e) => (
              <button
                key={e}
                data-testid={`react-${msg.id}-${e}`}
                onClick={() => { onReact?.(e); setPickerOpen(false); }}
                className="text-lg hover:scale-125 transition"
              >{e}</button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        data-testid={`reply-${msg.id}`}
        onClick={onReply}
        className="h-6 w-6 rounded-full bg-[#1a1a1a] border border-white/10 hover:border-[#d4af37]/40 text-neutral-300 flex items-center justify-center"
        title="Responder"
      ><Reply className="w-3 h-3" /></button>
      <button
        type="button"
        data-testid={`more-${msg.id}`}
        onClick={() => setOpen((o) => !o)}
        className="h-6 w-6 rounded-full bg-[#1a1a1a] border border-white/10 hover:border-[#d4af37]/40 text-neutral-300 flex items-center justify-center"
        title="Más"
      ><MoreHorizontal className="w-3 h-3" /></button>
      {open && (
        <div data-testid={`more-menu-${msg.id}`} className={`absolute top-7 ${isOwn ? "right-0" : "left-0"} bg-[#1a1a1a] border border-white/10 rounded-lg shadow-2xl z-[110] min-w-[140px] py-1`}>
          {canEdit && (
            <button data-testid={`edit-${msg.id}`} onClick={() => { onEdit?.(); setOpen(false); }} className="w-full text-left text-xs px-3 py-1.5 hover:bg-white/5 text-neutral-200 flex items-center gap-2"><Edit2 className="w-3 h-3" /> Editar</button>
          )}
          {canDelete && (
            <button data-testid={`delete-${msg.id}`} onClick={() => { onDelete?.(); setOpen(false); }} className="w-full text-left text-xs px-3 py-1.5 hover:bg-red-500/10 text-red-300 flex items-center gap-2"><Trash2 className="w-3 h-3" /> Eliminar</button>
          )}
          {canViewHistory && (
            <button data-testid={`history-${msg.id}`} onClick={() => { onShowHistory?.(); setOpen(false); }} className="w-full text-left text-xs px-3 py-1.5 hover:bg-white/5 text-amber-300 flex items-center gap-2"><Eye className="w-3 h-3" /> Ver historial</button>
          )}
          {!canEdit && !canDelete && !canViewHistory && (
            <div className="text-[10px] text-neutral-500 px-3 py-1.5 italic">Sin acciones disponibles</div>
          )}
        </div>
      )}
    </div>
  );
};


export const MessageHistoryModal = ({ mid, authHeader, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!mid) return;
    setLoading(true);
    axios.get(`${API}/admin/messages/${mid}/history`, { headers: authHeader() })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [mid, authHeader]);

  if (!mid) return null;

  return (
    <div data-testid="message-history-modal" className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-amber-500/40 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-amber-500/10">
          <h3 className="font-serif text-lg text-white flex items-center gap-2"><Eye className="w-4 h-4 text-amber-300" /> Historial de Modificaciones</h3>
          <button data-testid="history-close" onClick={onClose} className="h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
        </div>
        {loading || !data ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-amber-300 animate-spin" /></div>
        ) : (
          <div className="p-4 space-y-3 text-sm">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Versión actual</div>
              <div className="text-white whitespace-pre-wrap">{data.deleted ? <em className="text-red-300">[mensaje eliminado por {data.deleted_by_role} el {new Date(data.deleted_at).toLocaleString()}]</em> : data.current_body || <em className="text-neutral-500">(vacío)</em>}</div>
              <div className="text-[10px] text-neutral-500 mt-1">Editado: {data.edited_at ? new Date(data.edited_at).toLocaleString() : "—"}</div>
            </div>
            {data.history.length === 0 ? (
              <p className="text-neutral-500 italic">Sin ediciones previas.</p>
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-widest text-amber-300">Versiones anteriores ({data.history.length})</div>
                {[...data.history].reverse().map((h, i) => (
                  <div key={i} data-testid={`history-item-${i}`} className="bg-black/40 border border-white/10 rounded-lg p-3">
                    <div className="text-[10px] text-neutral-400">{new Date(h.at).toLocaleString()} · {h.by_role}: <span className="text-white">{h.by}</span></div>
                    <div className="text-neutral-200 whitespace-pre-wrap mt-1">{h.body || <em className="text-neutral-500">(vacío)</em>}</div>
                  </div>
                ))}
              </>
            )}
            <div className="text-[10px] text-neutral-500 pt-2 border-t border-white/5">Mensaje creado: {data.created_at ? new Date(data.created_at).toLocaleString() : "—"} por <span className="text-white">{data.from_role}</span></div>
          </div>
        )}
      </div>
    </div>
  );
};


/** Returns true if the message was sent less than 60 seconds ago. */
export const withinMinute = (createdAt) => {
  if (!createdAt) return false;
  try {
    const t = new Date(createdAt).getTime();
    return (Date.now() - t) < 60 * 1000;
  } catch { return false; }
};
